/* eslint-disable sonarjs/cognitive-complexity */

import React from 'react';

import { FormCandidate } from '../../shared/components/form-candidate/form-candidate';
import QuestionnaireInterviewCommon from '../../shared/components/questionnaire-interview-common/questionnaire-interview-common';
import QuestionnaireInterviewStartup from '../../shared/components/questionnaire-interview-startup/questionnaire-interview-startup';
import { RecomendationForInterview } from '../../shared/components/recomendation-for-interview/recomendation-for-interview';
import { TermsAndConditions } from '../../shared/components/terms-and-conditions/terms-and-conditions';
import { VideoApresentation } from '../../shared/components/video-apresentation/video-apresentation';
import { useStepManager } from '../../shared/hooks/useStepManager';

import {
  MapReponserMethod,
  useParamSystemController,
} from '../../../hooks/controllers/param-system-controller';
import { Chat } from '../../shared/components/chat/chat';
import ContentResultError from '../../shared/components/content-result-error/content-result-error';
import ContentResultProcessing from '../../shared/components/content-result-processing/content-result-processing';
import ContentResultSuccess from '../../shared/components/content-result-success/content-result-success';
import { useInterviewContext } from '../../shared/context/interview-context';

const MAP_COLORS_STATUS_INTERVIEW = {
  PROCESSING: '#FFC700',
  ERROR: '#F73450',
  SUCCESS: '#4FAD4F',
};

export const useInterviewSteps = (moduleId) => {
  const { interview } = useInterviewContext();
  const { findRequestMethodByInterview } = useParamSystemController();

  const { currentStep, goToNext, goToPrevious, goToStep } = useStepManager();
  const [activeQuestionId, setActiveQuestionId] = React.useState(1);
  const [finishedInterview, setFinishedInterview] = React.useState(false);
  const [statusInterview, setStatusInterview] = React.useState(null);
  const [responserMethod, setResponserMethod] = React.useState();

  const MAP_CONTENT_STATUS_INTERVIEW = React.useMemo(
    () => ({
      ERROR: <ContentResultError moduleId={interview?.module.id} />,
      SUCCESS: <ContentResultSuccess />,
      PROCESSING: <ContentResultProcessing moduleId={interview?.module.id} />,
    }),
    [interview],
  );

  React.useEffect(() => {
    if (interview == null) return;

    const fetchMethod = async () => {
      try {
        const response = await findRequestMethodByInterview(interview.id);
        setResponserMethod(response);
      } catch (error) {
        console.error('Erro ao buscar método:', error);
      }
    };

    fetchMethod();
  }, [interview, findRequestMethodByInterview]);

  React.useEffect(() => {
    if (interview === null) return;

    if (!interview.viewedVideo) {
      goToStep(1);
      return;
    }

    if (!interview.acceptedTerm) {
      goToStep(2);
      return;
    }

    if (!interview.candidate.emergencyContact) {
      goToStep(3);
      return;
    }

    const isFinished = ['FINALIZADA', 'EXPIRADA'].includes(
      interview.statusCompletingInterview,
    );

    if (interview.module.id === 2) {
      if (isFinished) {
        setFinishedInterview(true);
        setStatusInterview('SUCCESS');
        goToStep(4);
        return;
      }
      goToStep(4);
      return;
    }

    if (interview.statusCompletingInterview === 'EM_PREENCHIMENTO') {
      goToStep(5);
      return;
    }

    if (isFinished) {
      setFinishedInterview(true);
      setStatusInterview('PROCESSING');
      goToStep(5);
      return;
    }
    goToStep(4);
  }, [goToStep, interview]);

  const getContentStatusInterview = React.useCallback(() => {
    return MAP_CONTENT_STATUS_INTERVIEW[statusInterview] || <div>asd</div>;
  }, [statusInterview, MAP_CONTENT_STATUS_INTERVIEW]);

  const getColorStatusInterview = React.useCallback(() => {
    return MAP_COLORS_STATUS_INTERVIEW[statusInterview] || '#fff';
  }, [statusInterview]);

  const getQuestionnaireComponent = React.useCallback(() => {
    const shouldShowChat =
      (moduleId === 1 || moduleId === 3) &&
      responserMethod === MapReponserMethod.WEBCHAT;

    if (shouldShowChat) {
      return <Chat moduleId={moduleId} />;
    }

    const moduleComponents = {
      1: (
        <QuestionnaireInterviewCommon
          setFinishedInterview={setFinishedInterview}
          onQuestionChange={setActiveQuestionId}
          setStatusInterview={setStatusInterview}
        />
      ),
      2: (
        <QuestionnaireInterviewStartup
          setFinishedInterview={setFinishedInterview}
          setStatusInterview={setStatusInterview}
        />
      ),
      3: (
        <QuestionnaireInterviewCommon
          setFinishedInterview={setFinishedInterview}
          onQuestionChange={setActiveQuestionId}
          setStatusInterview={setStatusInterview}
        />
      ),
      4: (
        <QuestionnaireInterviewCommon
          setFinishedInterview={setFinishedInterview}
          onQuestionChange={setActiveQuestionId}
          setStatusInterview={setStatusInterview}
        />
      ),
    };

    return (
      moduleComponents[moduleId] || (
        <QuestionnaireInterviewCommon onQuestionChange={setActiveQuestionId} />
      )
    );
  }, [moduleId, responserMethod]);

  // const stepContents = React.useMemo(
  //   () => ({
  //     1: <VideoApresentation />,
  //     2: <TermsAndConditions />,
  //     3: <FormCandidate />,
  //     4: <RecomendationForInterview />,
  //     5: getQuestionnaireComponent(),
  //   }),
  //   [getQuestionnaireComponent],
  // );

  const stepContents = React.useMemo(() => {
    if (moduleId === 2) {
      return {
        1: <VideoApresentation />,
        2: <TermsAndConditions />,
        3: <FormCandidate />,
        4: getQuestionnaireComponent(),
      };
    }

    return {
      1: <VideoApresentation />,
      2: <TermsAndConditions />,
      3: <FormCandidate />,
      4: <RecomendationForInterview />,
      5: getQuestionnaireComponent(),
    };
  }, [moduleId, getQuestionnaireComponent]);

  return {
    currentStep,
    goToNext,
    goToPrevious,
    activeQuestionId,
    finishedInterview,
    setFinishedInterview,
    stepContents,
    getContentStatusInterview,
    setStatusInterview,
    getColorStatusInterview,
    statusInterview,
    goToStep,
  };
};
